import { collection, addDoc, doc, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

export type RecipientRole = 'buyer' | 'seller' | 'super_admin' | 'mediator' | 'support_agent' | 'user';

export interface MergeTag {
  tag: string;
  label: string;
  sample: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  category: 'Escrow & Orders' | 'Disputes & Arbitration' | 'Security & Auth' | 'Governance & Staff' | 'Trust & KYC' | 'Support & Helpdesk' | 'Onboarding & Lifecycle';
  description: string;
  enabled: boolean;
  senderName: string;
  senderAddress: string;
  replyTo: string;
  recipientRoles: RecipientRole[];
  customCc?: string[];
  customBcc?: string[];
  subject: string;
  preheader: string;
  heading: string;
  bodyHtml: string;
  availableMergeTags: MergeTag[];
  updatedAt?: any;
  updatedBy?: string;
}

export interface EmailPayload {
  to: string | string[];
  subject: string;
  html: string;
  from?: string;
  replyTo?: string;
  cc?: string[];
  bcc?: string[];
  type?: string;
}

// -----------------------------------------------------------------------------
// DEFAULT WOOCOMMERCE-STYLE EMAIL TEMPLATES
// -----------------------------------------------------------------------------

export const DEFAULT_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'user_welcome_onboarding',
    name: 'Customer Onboarding & Welcome Appreciation',
    category: 'Onboarding & Lifecycle',
    description: 'Dispatched to new buyers or sellers upon account creation. Delivers a personal appreciation message addressed to the customer, escrow safety guarantee, welcome discount voucher, and app exploration links.',
    enabled: true,
    senderName: 'ReUseIt Community & Onboarding',
    senderAddress: 'onboarding@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['buyer', 'seller', 'user'],
    customCc: [],
    customBcc: [],
    subject: 'Welcome to ReUseIt, {customer_name}! Thank you for joining circular commerce 🌱',
    preheader: 'Thank you for joining our community! Enjoy ₦{welcome_bonus} off your first escrow transaction.',
    heading: 'Welcome to ReUseIt, {customer_name}!',
    availableMergeTags: [
      { tag: '{customer_name}', label: 'Customer Full Name', sample: 'Amina Yusuf' },
      { tag: '{account_type}', label: 'Account Type', sample: 'Smart Buyer & Eco Trader' },
      { tag: '{welcome_bonus}', label: 'Welcome Escrow Bonus', sample: '1,000' },
      { tag: '{voucher_code}', label: 'Voucher Code', sample: 'WELCOME-REUSEIT-2026' },
      { tag: '{app_url}', label: 'Marketplace Feed', sample: 'https://reuseit.shop/market' },
      { tag: '{support_email}', label: 'Customer Care Email', sample: 'support@mail.reuseit.shop' },
    ],
    bodyHtml: `
<p>Dear <strong>{customer_name}</strong>,</p>

<p>
  On behalf of everyone at <strong>ReUseIt</strong>, we want to say a heartfelt <strong>thank you</strong> for creating an account with us as a <strong>{account_type}</strong>!
</p>

<p>
  Every pre-loved smartphone, laptop, home appliance, and fashion item re-homed through ReUseIt keeps valuable electronics out of landfills, cuts carbon emissions, and builds a sustainable, thriving circular economy across Nigeria.
</p>

<!-- Welcome Perk Box -->
<div style="background: linear-gradient(135deg, rgba(13,242,166,0.12) 0%, rgba(16,35,29,0.8) 100%); border: 1px solid #0df2a6; border-radius: 16px; padding: 22px; margin: 24px 0; text-align: center;">
  <span style="font-size: 11px; font-weight: 800; color: #0df2a6; text-transform: uppercase; letter-spacing: 1px;">Exclusive Welcome Gift</span>
  <h3 style="color: #ffffff; margin: 8px 0 4px 0; font-size: 22px; font-weight: 900;">₦{welcome_bonus} Escrow Fee Credit</h3>
  <p style="color: #cbd5e1; font-size: 13px; margin: 0 0 14px 0;">Use promo voucher at checkout for your first protected order:</p>
  <div style="display: inline-block; background: #08130f; border: 1px dashed #0df2a6; padding: 8px 20px; border-radius: 8px; font-family: monospace; font-size: 16px; font-weight: bold; color: #0df2a6; letter-spacing: 2px;">
    {voucher_code}
  </div>
</div>

<!-- Why ReUseIt Card -->
<div style="background: #152a23; border: 1px solid #22493c; border-radius: 14px; padding: 20px; margin: 24px 0;">
  <h4 style="color: #0df2a6; margin: 0 0 12px 0; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">🛡️ How You Are 100% Protected On ReUseIt</h4>
  <ul style="margin: 0; padding-left: 20px; color: #cbd5e1; font-size: 13px; line-height: 1.8;">
    <li><strong>Autonomous 48-Hour Escrow:</strong> When you buy, your payment stays safe in our escrow vault. The vendor is only paid after you inspect and approve the package.</li>
    <li><strong>Verified Delivery:</strong> Integrated courier tracking with GIG Logistics, Gokada, and Kwik gives real-time waybill status.</li>
    <li><strong>Fair Dispute Mediation:</strong> If an item differs from its description, our dedicated tribunal mediates a full refund or return.</li>
  </ul>
</div>

<p style="text-align: center; margin: 32px 0;">
  <a href="{app_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 800; text-decoration: none; padding: 15px 32px; border-radius: 12px; display: inline-block; font-size: 15px; box-shadow: 0 4px 14px rgba(13,242,166,0.3);">
    Start Exploring ReUseIt Market
  </a>
</p>

<p style="font-size: 13px; color: #90cbb7; line-height: 1.6;">
  Need assistance or have a question? Our support team is always here for you at <a href="mailto:{support_email}" style="color: #0df2a6; text-decoration: underline;">{support_email}</a>.
</p>

<p style="font-size: 13px; color: #ffffff; margin-top: 24px;">
  With gratitude,<br />
  <strong>The ReUseIt Team</strong><br />
  <span style="font-size: 11px; color: #90cbb7;">Victoria Island, Lagos, Nigeria</span>
</p>
    `.trim(),
  },
  {
    id: 'escrow_locked',
    name: 'Escrow Locked & Payment Confirmed',
    category: 'Escrow & Orders',
    description: 'Triggered when a buyer successfully funds an escrow transaction. Informs buyer funds are safe and instructs seller to pack & dispatch.',
    enabled: true,
    senderName: 'ReUseIt Escrow Desk',
    senderAddress: 'orders@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['buyer', 'seller', 'super_admin'],
    customCc: ['escrow-audit@reuseit.shop'],
    customBcc: [],
    subject: 'Escrow Secured: Payment confirmed for Order #{order_id}',
    preheader: 'Your payment of ₦{amount} is safely locked in escrow until inspection.',
    heading: 'Payment Secured in Escrow',
    availableMergeTags: [
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB Sierra Blue' },
      { tag: '{amount}', label: 'Order Amount', sample: '485,000' },
      { tag: '{buyer_name}', label: 'Buyer Full Name', sample: 'Kemi Adeleke' },
      { tag: '{seller_name}', label: 'Seller Store Name', sample: 'TechHaven Lagos' },
      { tag: '{delivery_address}', label: 'Delivery Location', sample: '14 Admiralty Way, Lekki Phase 1, Lagos' },
      { tag: '{app_url}', label: 'Application URL', sample: 'https://reuseit.shop/orders/RU-882194' },
    ],
    bodyHtml: `
<p>Hello <strong>{buyer_name}</strong> and <strong>{seller_name}</strong>,</p>
<p>This is official confirmation that payment for <strong>Order #{order_id}</strong> has been received and safely locked in the ReUseIt Escrow Vault.</p>
<table style="width: 100%; border-collapse: collapse; margin: 20px 0; background: #152a23; border-radius: 12px; overflow: hidden;">
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Item</td>
    <td style="padding: 12px 16px; color: #ffffff; font-weight: 600; font-size: 13px;">{item_title}</td>
  </tr>
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Escrow Protected Amount</td>
    <td style="padding: 12px 16px; color: #0df2a6; font-weight: bold; font-size: 15px;">₦{amount}</td>
  </tr>
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Delivery Destination</td>
    <td style="padding: 12px 16px; color: #ffffff; font-size: 13px;">{delivery_address}</td>
  </tr>
  <tr>
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Next Step</td>
    <td style="padding: 12px 16px; color: #f5f5f0; font-size: 13px;"><strong>Seller:</strong> Please package and dispatch via verified courier.</td>
  </tr>
</table>
<div style="background: rgba(13,242,166,0.08); border-left: 4px solid #0df2a6; padding: 14px 16px; margin: 20px 0; border-radius: 4px;">
  <p style="margin: 0; font-size: 13px; color: #d1fae5; line-height: 1.5;">
    <strong>Buyer Protection Guarantee:</strong> The seller will not receive payment until you receive and verify the item during the 48-hour inspection period.
  </p>
</div>
<p style="text-align: center; margin: 30px 0;">
  <a href="{app_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">View Escrow Order</a>
</p>
    `.trim(),
  },
  {
    id: 'order_dispatched',
    name: 'Order Dispatched & Tracking Active',
    category: 'Escrow & Orders',
    description: 'Sent to the buyer when the seller uploads logistics dispatch info and waybill tracking details.',
    enabled: true,
    senderName: 'ReUseIt Logistics Desk',
    senderAddress: 'orders@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['buyer', 'super_admin'],
    customCc: [],
    customBcc: [],
    subject: 'On the way! Order #{order_id} has been dispatched via {courier_name}',
    preheader: 'Your package is on its way with tracking code {tracking_number}.',
    heading: 'Your Order is in Transit',
    availableMergeTags: [
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB' },
      { tag: '{buyer_name}', label: 'Buyer Name', sample: 'Kemi Adeleke' },
      { tag: '{seller_name}', label: 'Seller Name', sample: 'TechHaven Lagos' },
      { tag: '{courier_name}', label: 'Courier Service', sample: 'GIG Logistics (GIGL)' },
      { tag: '{tracking_number}', label: 'Waybill Number', sample: 'GIG-99824102-NG' },
      { tag: '{est_delivery_date}', label: 'Est. Delivery Date', sample: '2 Business Days' },
      { tag: '{app_url}', label: 'Live Tracking Link', sample: 'https://reuseit.shop/track/GIG-99824102-NG' },
    ],
    bodyHtml: `
<p>Hello <strong>{buyer_name}</strong>,</p>
<p>Great news! <strong>{seller_name}</strong> has dispatched your package for <strong>Order #{order_id}</strong>.</p>
<div style="background: #152a23; border: 1px solid #22493c; border-radius: 12px; padding: 20px; margin: 20px 0;">
  <p style="margin: 0 0 8px 0; color: #90cbb7; font-size: 13px;">Logistics Courier:</p>
  <p style="margin: 0 0 16px 0; color: #ffffff; font-size: 16px; font-weight: bold;">{courier_name}</p>
  <p style="margin: 0 0 8px 0; color: #90cbb7; font-size: 13px;">Waybill / Tracking Code:</p>
  <p style="margin: 0 0 16px 0; font-family: monospace; color: #0df2a6; font-size: 18px; font-weight: bold; letter-spacing: 1px;">{tracking_number}</p>
  <p style="margin: 0 0 8px 0; color: #90cbb7; font-size: 13px;">Estimated Transit Time:</p>
  <p style="margin: 0; color: #f5f5f0; font-size: 14px;">{est_delivery_date}</p>
</div>
<p style="font-size: 13px; color: #90cbb7;">When the package arrives, the courier will hand it to you and the 48-hour inspection countdown will begin.</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{app_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">Track Delivery on ReUseIt</a>
</p>
    `.trim(),
  },
  {
    id: 'inspection_window_start',
    name: '48-Hour Inspection Window Initiated',
    category: 'Escrow & Orders',
    description: 'Triggered when the courier marks the package delivered. Buyer is given 48 hours to inspect and test the item.',
    enabled: true,
    senderName: 'ReUseIt Quality & Inspection',
    senderAddress: 'orders@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['buyer', 'seller'],
    customCc: [],
    customBcc: [],
    subject: 'Delivered: Order #{order_id} - Your 48-Hour Inspection Window is Active',
    preheader: 'Please inspect your package. Escrow will auto-settle after {inspection_deadline}.',
    heading: 'Package Delivered • 48h Inspection Open',
    availableMergeTags: [
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB' },
      { tag: '{buyer_name}', label: 'Buyer Name', sample: 'Kemi Adeleke' },
      { tag: '{seller_name}', label: 'Seller Name', sample: 'TechHaven Lagos' },
      { tag: '{inspection_deadline}', label: 'Inspection Deadline', sample: 'Saturday, 12:00 PM' },
      { tag: '{amount}', label: 'Escrow Amount', sample: '485,000' },
      { tag: '{app_url}', label: 'Action URL', sample: 'https://reuseit.shop/orders/RU-882194/inspect' },
    ],
    bodyHtml: `
<p>Hello <strong>{buyer_name}</strong>,</p>
<p>Your item <strong>{item_title}</strong> (Order #{order_id}) has been delivered! Your 48-Hour Inspection Window is officially open.</p>
<div style="background: #18221d; border: 1px solid #f59e0b; border-radius: 12px; padding: 20px; margin: 20px 0;">
  <p style="margin: 0 0 6px 0; color: #fbbf24; font-weight: bold; font-size: 14px;">⏳ Inspection Deadline: {inspection_deadline}</p>
  <p style="margin: 0; color: #e2e8f0; font-size: 13px; line-height: 1.5;">
    Please inspect the item thoroughly to confirm it matches the seller's condition description. If everything is satisfactory, tap <strong>"Accept & Release Funds"</strong>. If there are discrepancies, you can file a dispute before the deadline expires.
  </p>
</div>
<p style="font-size: 13px; color: #90cbb7;">
  <strong>Notice to {seller_name}:</strong> The buyer has received the item. Once approved or when the 48-hour timer reaches zero without dispute, your payout of ₦{amount} (net of platform fee) will automatically transfer to your linked bank account.
</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{app_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">Inspect & Confirm Item</a>
</p>
    `.trim(),
  },
  {
    id: 'payout_settled',
    name: 'Vendor Payout Remittance Settled',
    category: 'Escrow & Orders',
    description: 'Sent to the vendor when funds are automatically or manually disbursed into their Nigerian bank account.',
    enabled: true,
    senderName: 'ReUseIt Settlement Desk',
    senderAddress: 'orders@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['seller', 'super_admin'],
    customCc: ['payouts@reuseit.shop'],
    customBcc: [],
    subject: 'Payout Remitted: ₦{net_payout} for Order #{order_id}',
    preheader: 'Your payout has been transferred to your bank account {account_masked}.',
    heading: 'Vendor Settlement Completed',
    availableMergeTags: [
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB' },
      { tag: '{seller_name}', label: 'Seller Name', sample: 'TechHaven Lagos' },
      { tag: '{gross_amount}', label: 'Gross Sale', sample: '485,000' },
      { tag: '{platform_fee}', label: 'Total Deduction (8.0625%)', sample: '39,103' },
      { tag: '{net_payout}', label: 'Net Remitted', sample: '445,897' },
      { tag: '{bank_name}', label: 'Bank Name', sample: 'Guaranty Trust Bank (GTB)' },
      { tag: '{account_masked}', label: 'Account Masked', sample: '••••••4921' },
      { tag: '{app_url}', label: 'Ledger Link', sample: 'https://reuseit.shop/vendor/wallet' },
    ],
    bodyHtml: `
<p>Hello <strong>{seller_name}</strong>,</p>
<p>Congratulations! Escrow has cleared for <strong>Order #{order_id}</strong> ({item_title}) and your payout remittance has been sent to your bank account.</p>
<table style="width: 100%; border-collapse: collapse; margin: 20px 0; background: #152a23; border-radius: 12px; overflow: hidden;">
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Gross Sale Amount</td>
    <td style="padding: 12px 16px; color: #ffffff; font-weight: 600; font-size: 13px;">₦{gross_amount}</td>
  </tr>
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Platform Fee + VAT</td>
    <td style="padding: 12px 16px; color: #f87171; font-size: 13px;">-₦{platform_fee}</td>
  </tr>
  <tr style="border-bottom: 1px solid #22493c;">
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Net Remittance Paid</td>
    <td style="padding: 12px 16px; color: #0df2a6; font-weight: bold; font-size: 16px;">₦{net_payout}</td>
  </tr>
  <tr>
    <td style="padding: 12px 16px; color: #90cbb7; font-size: 13px;">Destination Bank</td>
    <td style="padding: 12px 16px; color: #ffffff; font-size: 13px;">{bank_name} ({account_masked})</td>
  </tr>
</table>
<p style="font-size: 13px; color: #90cbb7;">NIP transfers typically reflect within 15–30 minutes depending on interbank clearing windows.</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{app_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">View Wallet & Statement</a>
</p>
    `.trim(),
  },
  {
    id: 'dispute_opened',
    name: 'Dispute Opened & Escrow Frozen',
    category: 'Disputes & Arbitration',
    description: 'Dispatched immediately when an issue is reported during the inspection window. Freezes funds and creates arbitration docket.',
    enabled: true,
    senderName: 'ReUseIt Mediation Tribunal',
    senderAddress: 'disputes@mail.reuseit.shop',
    replyTo: 'disputes@mail.reuseit.shop',
    recipientRoles: ['buyer', 'seller', 'mediator', 'super_admin'],
    customCc: ['tribunal@reuseit.shop'],
    customBcc: [],
    subject: '[Case #{dispute_id}] Escrow Frozen: Dispute Filed for Order #{order_id}',
    preheader: 'A dispute claim has been logged for {item_title}. Escrow payout is suspended.',
    heading: 'Dispute Logged • Escrow Suspended',
    availableMergeTags: [
      { tag: '{dispute_id}', label: 'Dispute Case ID', sample: 'DISP-7812' },
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB' },
      { tag: '{claimant_name}', label: 'Claimant Name', sample: 'Kemi Adeleke' },
      { tag: '{defendant_name}', label: 'Defendant Name', sample: 'TechHaven Lagos' },
      { tag: '{dispute_reason}', label: 'Reason for Dispute', sample: 'Item condition materially different (cracked screen)' },
      { tag: '{frozen_amount}', label: 'Frozen Escrow Sum', sample: '485,000' },
      { tag: '{tribunal_url}', label: 'Mediation Case Room', sample: 'https://admin.reuseit.shop/disputes/DISP-7812' },
    ],
    bodyHtml: `
<p>Attention <strong>{claimant_name}</strong>, <strong>{defendant_name}</strong>, and ReUseIt Mediation Officers,</p>
<p>An official dispute case has been registered regarding <strong>Order #{order_id}</strong> for <em>{item_title}</em>.</p>
<div style="background: rgba(239, 68, 68, 0.08); border-left: 4px solid #ef4444; padding: 16px; border-radius: 4px; margin: 20px 0;">
  <p style="margin: 0 0 6px 0; color: #fca5a5; font-weight: bold; font-size: 14px;">Case ID: #{dispute_id} • Escrow Funds Frozen</p>
  <p style="margin: 0 0 8px 0; color: #ffffff; font-size: 13px;"><strong>Reported Reason:</strong> {dispute_reason}</p>
  <p style="margin: 0; color: #fca5a5; font-size: 12px;">Protected Sum: <strong>₦{frozen_amount}</strong> is securely retained until verdict.</p>
</div>
<p style="font-size: 13px; color: #90cbb7;">
  Both parties have <strong>24 hours</strong> to upload supporting photo/video evidence and courier receipts into the ReUseIt Dispute Tribunal Room.
</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{tribunal_url}" style="background-color: #ef4444; color: #ffffff; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">Access Tribunal Docket</a>
</p>
    `.trim(),
  },
  {
    id: 'dispute_verdict',
    name: 'Dispute Resolved / Binding Verdict',
    category: 'Disputes & Arbitration',
    description: 'Official arbitration decision released by mediator detailing buyer refund, seller release, or partial split.',
    enabled: true,
    senderName: 'ReUseIt Mediation Tribunal',
    senderAddress: 'disputes@mail.reuseit.shop',
    replyTo: 'disputes@mail.reuseit.shop',
    recipientRoles: ['buyer', 'seller', 'mediator', 'super_admin'],
    customCc: ['tribunal@reuseit.shop'],
    customBcc: [],
    subject: 'Official Ruling: Resolution for Dispute #{dispute_id} (Order #{order_id})',
    preheader: 'The arbitration tribunal has rendered a binding verdict for Case #{dispute_id}.',
    heading: 'Arbitration Tribunal Final Verdict',
    availableMergeTags: [
      { tag: '{dispute_id}', label: 'Dispute ID', sample: 'DISP-7812' },
      { tag: '{order_id}', label: 'Order ID', sample: 'RU-882194' },
      { tag: '{item_title}', label: 'Item Name', sample: 'iPhone 13 Pro 128GB' },
      { tag: '{verdict_outcome}', label: 'Outcome Decree', sample: 'Full Buyer Refund upon Item Return' },
      { tag: '{refund_amount}', label: 'Buyer Refund', sample: '485,000' },
      { tag: '{seller_payout}', label: 'Seller Payout', sample: '0' },
      { tag: '{resolution_notes}', label: 'Mediator Findings', sample: 'Inspection verified screen damage present before courier transit.' },
      { tag: '{mediator_name}', label: 'Presiding Mediator', sample: 'Tola Adebayo (Senior Arbitrator)' },
    ],
    bodyHtml: `
<p>Official Arbitration Decree from <strong>{mediator_name}</strong>,</p>
<p>Following a review of evidence submitted for <strong>Case #{dispute_id}</strong> regarding <strong>Order #{order_id}</strong> ({item_title}), the ReUseIt Mediation Tribunal has rendered a final ruling.</p>
<div style="background: #152a23; border: 1px solid #22493c; border-radius: 12px; padding: 20px; margin: 20px 0;">
  <p style="margin: 0 0 6px 0; color: #90cbb7; font-size: 13px;">Decreed Outcome:</p>
  <p style="margin: 0 0 16px 0; color: #0df2a6; font-size: 17px; font-weight: bold;">{verdict_outcome}</p>
  <p style="margin: 0 0 6px 0; color: #90cbb7; font-size: 13px;">Findings & Settlement Basis:</p>
  <p style="margin: 0 0 16px 0; color: #ffffff; font-size: 13px; line-height: 1.5;">{resolution_notes}</p>
  <div style="display: flex; gap: 16px; border-top: 1px solid #22493c; padding-top: 14px;">
    <div>
      <span style="font-size: 11px; color: #90cbb7; text-transform: uppercase;">Buyer Refund</span>
      <p style="margin: 2px 0 0 0; font-size: 15px; font-weight: bold; color: #ffffff;">₦{refund_amount}</p>
    </div>
    <div style="margin-left: 24px;">
      <span style="font-size: 11px; color: #90cbb7; text-transform: uppercase;">Seller Payout</span>
      <p style="margin: 2px 0 0 0; font-size: 15px; font-weight: bold; color: #ffffff;">₦{seller_payout}</p>
    </div>
  </div>
</div>
<p style="font-size: 12px; color: #64748b;">This decree is legally binding under the ReUseIt Escrow Terms & Condition of Use.</p>
    `.trim(),
  },
  {
    id: 'admin_auth_otp',
    name: 'Admin 2FA Security OTP & Password Reset',
    category: 'Security & Auth',
    description: 'Dispatches 6-digit one-time passcode for admin logins and emergency password reset requests.',
    enabled: true,
    senderName: 'ReUseIt Security Operations',
    senderAddress: 'security@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['super_admin', 'mediator', 'support_agent'],
    customCc: [],
    customBcc: [],
    subject: 'Your ReUseIt Admin Security Passcode: {otp_code}',
    preheader: 'Use one-time verification code {otp_code} to authenticate your session. Valid for 10 minutes.',
    heading: 'Admin Security Verification',
    availableMergeTags: [
      { tag: '{admin_email}', label: 'Admin Email', sample: 'oliversammytwist@gmail.com' },
      { tag: '{otp_code}', label: '6-Digit OTP', sample: '849201' },
      { tag: '{expiry_minutes}', label: 'Expiry Duration', sample: '10' },
      { tag: '{ip_address}', label: 'Request IP', sample: '102.89.44.12' },
      { tag: '{device_info}', label: 'Device / Browser', sample: 'Chrome on Windows 11' },
    ],
    bodyHtml: `
<p>Hello Administrator,</p>
<p>A single-use security passcode has been requested for your administrative account at <strong>{admin_email}</strong>.</p>
<div style="background-color: #152a23; border: 1px solid #22493c; border-radius: 16px; padding: 24px; text-align: center; margin: 24px 0;">
  <div style="font-family: 'Courier New', Courier, monospace; font-size: 36px; font-weight: 900; letter-spacing: 8px; color: #0df2a6;">{otp_code}</div>
  <div style="font-size: 11px; color: #90cbb7; margin-top: 8px; text-transform: uppercase; font-weight: 700;">Single-Use Security OTP • {expiry_minutes}-Minute Expiry</div>
</div>
<div style="background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3); border-radius: 12px; padding: 14px; font-size: 12px; color: #fcd34d; line-height: 1.5; margin-bottom: 20px;">
  <strong>Security Notice:</strong> Never share this passcode with anyone. ReUseIt security teams will never ask for your verification code. Request originated from IP {ip_address} ({device_info}).
</div>
    `.trim(),
  },
  {
    id: 'staff_invitation',
    name: 'Staff Onboarding & Role Provisioning',
    category: 'Governance & Staff',
    description: 'Invitation sent to newly added administrative personnel with temporary credentials and 2FA instructions.',
    enabled: true,
    senderName: 'ReUseIt Team Operations',
    senderAddress: 'onboarding@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['super_admin', 'mediator', 'support_agent'],
    customCc: ['superadmin@reuseit.shop'],
    customBcc: [],
    subject: 'Welcome to ReUseIt Staff: Your {admin_role} Credentials',
    preheader: 'You have been granted administrative clearance as a {admin_role}.',
    heading: 'Staff Portal Clearance Granted',
    availableMergeTags: [
      { tag: '{admin_name}', label: 'Admin Name', sample: 'Tola Adebayo' },
      { tag: '{admin_email}', label: 'Login Email', sample: 'tola.adebayo@reuseit.shop' },
      { tag: '{admin_role}', label: 'Assigned Role', sample: 'Dispute Mediator' },
      { tag: '{temp_password}', label: 'Temporary Password', sample: 'SecurePass#2026!' },
      { tag: '{portal_url}', label: 'Portal URL', sample: 'https://admin.reuseit.shop' },
    ],
    bodyHtml: `
<p>Hello <strong>{admin_name}</strong>,</p>
<p>You have been provisioned administrative clearance on the <strong>ReUseIt Super Admin Portal</strong> with the role of <strong>{admin_role}</strong>.</p>
<div style="background-color: #152a23; padding: 20px; border-radius: 12px; margin: 20px 0; border: 1px solid #22493c;">
  <p style="margin: 0 0 10px 0; font-size: 13px; color: #90cbb7;">Portal URL: <a href="{portal_url}" style="color: #0df2a6; font-weight: bold;">{portal_url}</a></p>
  <p style="margin: 0 0 10px 0; font-size: 13px; color: #90cbb7;">Authorized Email: <strong style="color: #ffffff;">{admin_email}</strong></p>
  <p style="margin: 0; font-size: 13px; color: #90cbb7;">Temporary Password: <strong style="color: #ffffff; font-family: monospace; font-size: 15px;">{temp_password}</strong></p>
</div>
<p style="font-size: 13px; color: #90cbb7;">
  Upon initial login, you will be required to configure your <strong>2FA Authenticator App</strong> (e.g. Google Authenticator) and choose a permanent password.
</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{portal_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">Sign In to Staff Portal</a>
</p>
    `.trim(),
  },
  {
    id: 'kyc_status_update',
    name: 'Vendor KYC Identity Verification Outcome',
    category: 'Trust & KYC',
    description: 'Informs marketplace sellers whether their Tier 2/3 KYC government ID verification was approved or rejected.',
    enabled: true,
    senderName: 'ReUseIt Trust & Safety',
    senderAddress: 'notifications@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['seller', 'super_admin'],
    customCc: [],
    customBcc: [],
    subject: 'ReUseIt Trust & Verification: {kyc_status}',
    preheader: 'Status update regarding your vendor verification badge and trust level.',
    heading: 'KYC Verification Update',
    availableMergeTags: [
      { tag: '{user_name}', label: 'User Name', sample: 'Chinedu Okeke' },
      { tag: '{kyc_status}', label: 'KYC Status (Approved / Action Required)', sample: 'Approved (Tier 2 Silver)' },
      { tag: '{tier_name}', label: 'Tier Awarded', sample: 'Tier 2 Verified Vendor' },
      { tag: '{review_notes}', label: 'Compliance Notes', sample: 'NIN slip and facial selfie match government biometric records.' },
      { tag: '{trust_score}', label: 'Trust Score', sample: '90 / 100' },
      { tag: '{profile_url}', label: 'Profile Link', sample: 'https://reuseit.shop/profile/kyc' },
    ],
    bodyHtml: `
<p>Hello <strong>{user_name}</strong>,</p>
<p>The ReUseIt Trust & Safety compliance desk has completed review of your identity verification documents.</p>
<div style="background: #152a23; border: 1px solid #22493c; border-radius: 12px; padding: 20px; margin: 20px 0;">
  <p style="margin: 0 0 6px 0; color: #90cbb7; font-size: 13px;">Verification Status:</p>
  <p style="margin: 0 0 14px 0; color: #0df2a6; font-size: 18px; font-weight: bold;">{kyc_status}</p>
  <p style="margin: 0 0 6px 0; color: #90cbb7; font-size: 13px;">Compliance Evaluation Notes:</p>
  <p style="margin: 0 0 14px 0; color: #ffffff; font-size: 13px; line-height: 1.5;">{review_notes}</p>
  <p style="margin: 0; color: #90cbb7; font-size: 12px;">Assigned Trust Level: <strong style="color: #ffffff;">{tier_name} ({trust_score})</strong></p>
</div>
<p style="text-align: center; margin: 28px 0;">
  <a href="{profile_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">View Verification Badge</a>
</p>
    `.trim(),
  },
  {
    id: 'support_ticket_reply',
    name: 'Customer Support Helpdesk Reply',
    category: 'Support & Helpdesk',
    description: 'Notification sent to users when a customer care specialist or admin updates their support inquiry.',
    enabled: true,
    senderName: 'ReUseIt Customer Care',
    senderAddress: 'support@mail.reuseit.shop',
    replyTo: 'support@mail.reuseit.shop',
    recipientRoles: ['user', 'support_agent'],
    customCc: [],
    customBcc: [],
    subject: 'Re: [Ticket #{ticket_id}] {ticket_subject}',
    preheader: 'New reply from {agent_name} at ReUseIt Support.',
    heading: 'Support Inquiry Update',
    availableMergeTags: [
      { tag: '{ticket_id}', label: 'Ticket ID', sample: 'TICK-4921' },
      { tag: '{user_name}', label: 'Customer Name', sample: 'Fatima Bello' },
      { tag: '{ticket_subject}', label: 'Subject', sample: 'Question on Lagos Courier Pickup Fees' },
      { tag: '{agent_name}', label: 'Support Agent Name', sample: 'Sarah from ReUseIt Care' },
      { tag: '{agent_role}', label: 'Agent Title', sample: 'Senior Support Specialist' },
      { tag: '{reply_message}', label: 'Reply Body', sample: 'We have confirmed with GIG Logistics that the intra-state fee is capped at ₦2,500.' },
      { tag: '{ticket_url}', label: 'Ticket URL', sample: 'https://reuseit.shop/help/tickets/TICK-4921' },
    ],
    bodyHtml: `
<p>Hello <strong>{user_name}</strong>,</p>
<p>You have received a reply from <strong>{agent_name}</strong> ({agent_role}) on your support inquiry <strong>#{ticket_id}: {ticket_subject}</strong>.</p>
<div style="background: #152a23; border-left: 4px solid #0df2a6; border-radius: 4px; padding: 16px; margin: 20px 0;">
  <p style="margin: 0; color: #ffffff; font-size: 14px; line-height: 1.6; white-space: pre-wrap;">{reply_message}</p>
</div>
<p style="font-size: 13px; color: #90cbb7;">You can reply directly to this email or visit the support desk online.</p>
<p style="text-align: center; margin: 28px 0;">
  <a href="{ticket_url}" style="background-color: #0df2a6; color: #08130f; font-weight: 700; text-decoration: none; padding: 14px 28px; border-radius: 10px; display: inline-block; font-size: 14px;">Open Support Thread</a>
</p>
    `.trim(),
  },
];

// -----------------------------------------------------------------------------
// HTML LAYOUT BUILDER
// -----------------------------------------------------------------------------

export const buildFullEmailHtml = (
  heading: string,
  preheader: string,
  contentHtml: string,
  senderDomain: string = 'mail.reuseit.shop'
): string => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${heading}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      background-color: #08130f;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #f5f5f0;
    }
    .wrapper {
      max-width: 600px;
      margin: 32px auto;
      background-color: #10231d;
      border: 1px solid #22493c;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    }
    .preheader {
      display: none !important;
      visibility: hidden;
      mso-hide: all;
      font-size: 1px;
      line-height: 1px;
      max-height: 0;
      max-width: 0;
      opacity: 0;
      overflow: hidden;
    }
    .header {
      padding: 32px 32px 24px 32px;
      border-bottom: 1px solid #22493c;
      background: linear-gradient(180deg, rgba(13,242,166,0.06) 0%, rgba(16,35,29,0) 100%);
    }
    .brand {
      font-size: 22px;
      font-weight: 900;
      color: #ffffff;
      letter-spacing: -0.5px;
    }
    .brand-accent {
      color: #0df2a6;
    }
    .badge {
      display: inline-block;
      font-size: 11px;
      font-weight: 700;
      background: rgba(13,242,166,0.15);
      color: #0df2a6;
      padding: 4px 10px;
      border-radius: 99px;
      margin-left: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .body-content {
      padding: 32px;
      font-size: 14px;
      line-height: 1.6;
      color: #cbd5e1;
    }
    .body-content h1 {
      font-size: 22px;
      font-weight: 800;
      color: #ffffff;
      margin: 0 0 16px 0;
      letter-spacing: -0.3px;
    }
    .body-content p {
      margin: 0 0 16px 0;
      color: #cbd5e1;
    }
    .body-content strong {
      color: #ffffff;
    }
    .footer {
      padding: 24px 32px;
      border-top: 1px solid #22493c;
      background-color: #0d1e19;
      font-size: 11px;
      color: #64748b;
      text-align: center;
      line-height: 1.6;
    }
    .footer a {
      color: #0df2a6;
      text-decoration: none;
    }
  </style>
</head>
<body>
  <div class="preheader">${preheader}</div>
  <div class="wrapper">
    <div class="header">
      <span class="brand">ReUse<span class="brand-accent">It</span></span>
      <span class="badge">Verified Transactional</span>
    </div>
    <div class="body-content">
      <h1>${heading}</h1>
      ${contentHtml}
    </div>
    <div class="footer">
      <p style="margin: 0 0 6px 0;">
        Securely dispatched via Zoho ZeptoMail SMTP gateway (<strong>${senderDomain}</strong>).
      </p>
      <p style="margin: 0;">
        © ${new Date().getFullYear()} ReUseIt Re-Commerce Inc. • Victoria Island, Lagos, Nigeria • <a href="https://reuseit.shop">reuseit.shop</a>
      </p>
    </div>
  </div>
</body>
</html>
  `.trim();
};

export const substituteMergeTags = (
  templateText: string,
  mergeValues: Record<string, string>
): string => {
  let result = templateText;
  Object.entries(mergeValues).forEach(([tag, val]) => {
    result = result.split(tag).join(val);
  });
  return result;
};

// -----------------------------------------------------------------------------
// FIRESTORE PERSISTENCE & DISPATCH APIS
// -----------------------------------------------------------------------------

export const getEmailTemplates = async (): Promise<EmailTemplate[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'email_templates'));
    if (snapshot.empty) {
      return DEFAULT_EMAIL_TEMPLATES;
    }
    const persistedTemplates = new Map<string, any>();
    snapshot.docs.forEach((d) => persistedTemplates.set(d.id, { id: d.id, ...d.data() }));

    return DEFAULT_EMAIL_TEMPLATES.map((defaultTmpl) => {
      const persisted = persistedTemplates.get(defaultTmpl.id);
      if (persisted) {
        return {
          ...defaultTmpl,
          ...persisted,
          availableMergeTags: defaultTmpl.availableMergeTags,
        };
      }
      return defaultTmpl;
    });
  } catch (err) {
    console.warn('Could not load email_templates from Firestore, falling back to defaults:', err);
    return DEFAULT_EMAIL_TEMPLATES;
  }
};

export const saveEmailTemplate = async (
  template: EmailTemplate,
  adminEmail: string = 'superadmin@reuseit.shop'
): Promise<void> => {
  try {
    const templateRef = doc(db, 'email_templates', template.id);
    await setDoc(
      templateRef,
      {
        ...template,
        updatedAt: serverTimestamp(),
        updatedBy: adminEmail,
      },
      { merge: true }
    );
  } catch (error) {
    console.error(`Failed to save template ${template.id}:`, error);
    throw error;
  }
};

export const sendTransactionalEmail = async (payload: EmailPayload): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'mail'), {
      to: payload.to,
      from: payload.from || 'ReUseIt Security <security@mail.reuseit.shop>',
      replyTo: payload.replyTo || 'support@mail.reuseit.shop',
      cc: payload.cc || [],
      bcc: payload.bcc || [],
      type: payload.type || 'transactional',
      message: {
        subject: payload.subject,
        html: payload.html,
      },
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Failed to enqueue transactional email in Firestore:', error);
    throw error;
  }
};

export const sendLiveTestEmail = async (
  template: EmailTemplate,
  targetEmail: string,
  sampleValues?: Record<string, string>
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  const values: Record<string, string> = {};
  template.availableMergeTags.forEach((tag) => {
    values[tag.tag] = sampleValues?.[tag.tag] || tag.sample;
  });

  const resolvedSubject = substituteMergeTags(template.subject, values);
  const resolvedHeading = substituteMergeTags(template.heading, values);
  const resolvedPreheader = substituteMergeTags(template.preheader, values);
  const resolvedBody = substituteMergeTags(template.bodyHtml, values);

  const fullHtml = buildFullEmailHtml(
    resolvedHeading,
    resolvedPreheader,
    resolvedBody,
    template.senderAddress.split('@')[1] || 'mail.reuseit.shop'
  );

  try {
    const testCallable = httpsCallable(functions, 'testZeptoMailConnection');
    const res: any = await testCallable({
      targetEmail,
      templateKey: template.id,
      customSubject: `[TEST] ${resolvedSubject}`,
      customHtml: fullHtml,
    });
    return {
      success: true,
      messageId: res.data?.messageId,
    };
  } catch (callableError: any) {
    console.warn('Callable test function failed, falling back to Firestore /mail queue:', callableError);
    try {
      const mailDocId = await sendTransactionalEmail({
        to: targetEmail,
        from: `"${template.senderName}" <${template.senderAddress}>`,
        replyTo: template.replyTo,
        subject: `[TEST] ${resolvedSubject}`,
        html: fullHtml,
        type: `test_${template.id}`,
      });
      return {
        success: true,
        messageId: `firestore-queue:${mailDocId}`,
      };
    } catch (fallbackError: any) {
      return {
        success: false,
        error: fallbackError?.message || callableError?.message || 'Failed to dispatch test email',
      };
    }
  }
};

export const sendAdminOtpEmail = async (adminEmail: string, otpCode: string): Promise<string> => {
  const template = DEFAULT_EMAIL_TEMPLATES.find((t) => t.id === 'admin_auth_otp');
  const values: Record<string, string> = {
    '{admin_email}': adminEmail,
    '{otp_code}': otpCode,
    '{expiry_minutes}': '10',
    '{ip_address}': '102.89.44.12',
    '{device_info}': 'Admin Portal Web Session',
  };

  const subject = template ? substituteMergeTags(template.subject, values) : `Your Security OTP: ${otpCode}`;
  const body = template ? substituteMergeTags(template.bodyHtml, values) : `<p>Your code is <strong>${otpCode}</strong></p>`;
  const html = buildFullEmailHtml('Admin Security Verification', 'Single use OTP code', body);

  return sendTransactionalEmail({
    to: adminEmail,
    from: 'ReUseIt Security <security@mail.reuseit.shop>',
    replyTo: 'support@mail.reuseit.shop',
    subject,
    html,
    type: 'admin_otp',
  });
};

export const sendStaffInvitationEmail = async (
  email: string,
  name: string,
  roleName: string,
  tempPass: string
): Promise<string> => {
  const template = DEFAULT_EMAIL_TEMPLATES.find((t) => t.id === 'staff_invitation');
  const values: Record<string, string> = {
    '{admin_name}': name,
    '{admin_email}': email,
    '{admin_role}': roleName,
    '{temp_password}': tempPass,
    '{portal_url}': 'https://admin.reuseit.shop',
  };

  const subject = template ? substituteMergeTags(template.subject, values) : `Your ReUseIt Admin Clearance`;
  const body = template ? substituteMergeTags(template.bodyHtml, values) : `<p>Welcome ${name}</p>`;
  const html = buildFullEmailHtml('Staff Clearance Provisioned', 'Welcome to ReUseIt Staff', body);

  return sendTransactionalEmail({
    to: email,
    from: 'ReUseIt Onboarding <onboarding@mail.reuseit.shop>',
    replyTo: 'support@mail.reuseit.shop',
    subject,
    html,
    type: 'staff_invitation',
  });
};

export const sendCustomerWelcomeEmail = async (
  email: string,
  customerName: string,
  accountType: string = 'Smart Buyer & Eco Trader'
): Promise<string> => {
  const template = DEFAULT_EMAIL_TEMPLATES.find((t) => t.id === 'user_welcome_onboarding');
  const values: Record<string, string> = {
    '{customer_name}': customerName,
    '{account_type}': accountType,
    '{welcome_bonus}': '1,000',
    '{voucher_code}': 'WELCOME-REUSEIT-2026',
    '{app_url}': 'https://reuseit.shop/market',
    '{support_email}': 'support@mail.reuseit.shop',
  };

  const subject = template ? substituteMergeTags(template.subject, values) : `Welcome to ReUseIt, ${customerName}!`;
  const heading = template ? substituteMergeTags(template.heading, values) : `Welcome to ReUseIt, ${customerName}!`;
  const preheader = template ? substituteMergeTags(template.preheader, values) : `Thank you for joining ReUseIt.`;
  const body = template ? substituteMergeTags(template.bodyHtml, values) : `<p>Welcome ${customerName}</p>`;
  const html = buildFullEmailHtml(heading, preheader, body);

  return sendTransactionalEmail({
    to: email,
    from: '"ReUseIt Community" <onboarding@mail.reuseit.shop>',
    replyTo: 'support@mail.reuseit.shop',
    subject,
    html,
    type: 'user_welcome_onboarding',
  });
};
